/**
 * Settling a client's month.
 *
 * Until now nothing ever closed. A month stopped being the current one and its
 * unfinished work sat in it forever, so "how did August go" was a question
 * whose answer kept changing — a reel finished in October still landed in
 * August's count, and a client's report was accurate only until somebody did
 * more work.
 *
 * Closing settles it, and the one real decision is what happens to the work
 * that did not get done. Both answers are honest and neither is safe to
 * assume, so this asks rather than defaulting:
 *
 *   - **Carry it** — the work is still wanted and somebody is still going to
 *     do it. It moves into next month and counts there.
 *   - **Leave it** — it stays where it is, and the month is on the record as
 *     one the studio did not deliver.
 *
 * It is reversible, and says so: reopening is one click. What reopening does
 * not undo is the carrying, because a piece that moved is a piece that moved.
 * The confirm says that too, because finding it out afterwards is worse.
 */

import { Lock, LockOpen } from 'lucide-react'
import { useState } from 'react'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { apiErrorMessage } from '../lib/api'
import { monthName } from '../lib/format'
import { shiftMonth } from './Requirements'

export function CloseMonth({
  clientId,
  clientName,
  month,
  closedAt,
  unfinished
}: {
  clientId: string
  clientName: string
  month: string
  /** When it was closed, or null while it is still open. */
  closedAt: string | null
  /** How many pieces in the month have not posted. */
  unfinished: number
}) {
  const { closeMonth, reopenMonth } = useAppData()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)

  // Closing changes what a month delivered and carrying moves work between two
  // months' counts. The server holds this line too; the point of holding it
  // here is not offering somebody a button they will be refused.
  const mayClose = currentUser?.role === 'PARTNER' || currentUser?.role === 'MANAGER'
  if (!mayClose) return null

  const next = shiftMonth(month, 1)

  async function settle(carry: boolean) {
    setBusy(true)
    try {
      const done = await closeMonth(clientId, month, carry)
      setAsking(false)
      const moved = done.carried > 0 ? `, ${done.carried} carried into ${monthName(next)}` : ''
      const left = done.missed > 0 ? `, ${done.missed} left unfinished` : ''
      showToast(`${monthName(month)} closed for ${clientName}: ${done.delivered} delivered${moved}${left}.`)
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function open() {
    setBusy(true)
    try {
      await reopenMonth(clientId, month)
      showToast(`${monthName(month)} is open again.`)
    } catch (err) {
      showToast(apiErrorMessage(err), 'error')
    } finally {
      setBusy(false)
    }
  }

  if (closedAt !== null) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => void open()}
        title={`Closed ${new Date(closedAt).toLocaleDateString()}`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-shell-border px-2.5 py-1.5 text-xs font-medium text-shell-text transition-colors hover:border-brand-500/50 hover:text-shell-text-active disabled:opacity-60"
      >
        <LockOpen size={13} /> Closed — open it again
      </button>
    )
  }

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-shell-border px-2.5 py-1.5 text-xs font-medium text-shell-text transition-colors hover:border-brand-500/50 hover:text-shell-text-active"
      >
        <Lock size={13} /> Close {monthName(month)}
      </button>
    )
  }

  return (
    <div className="w-full rounded-xl border border-shell-border bg-canvas-surface p-3 sm:w-auto sm:min-w-[22rem]">
      <p className="text-sm font-medium text-shell-text-active">
        Close {monthName(month)} for {clientName}?
      </p>
      <p className="mt-1 text-xs text-shell-text">
        {unfinished === 0
          ? 'Everything in it has posted. Closing settles the count so it stops changing.'
          : `${unfinished} ${unfinished === 1 ? 'piece has' : 'pieces have'} not posted. What should happen to ${unfinished === 1 ? 'it' : 'them'}?`}
      </p>

      <div className="mt-3 flex flex-col gap-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={() => void settle(true)}
          className="rounded-lg bg-brand-500 px-3 py-2 text-left text-xs font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
        >
          Carry {unfinished === 1 ? 'it' : 'the unfinished work'} into {monthName(next)}
          <span className="block font-normal opacity-80">Still wanted, and it counts there instead.</span>
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void settle(false)}
          className="rounded-lg border border-shell-border px-3 py-2 text-left text-xs font-medium text-shell-text-active transition-colors hover:bg-shell-border/40 disabled:opacity-60"
        >
          Leave {unfinished === 1 ? 'it' : 'them'} in {monthName(month)}
          <span className="block font-normal text-shell-text">
            On the record as a month the studio did not deliver.
          </span>
        </button>
      </div>

      <p className="mt-2 text-[11px] text-shell-text">
        Closing can be undone. Carrying cannot — a piece that moved has moved, and comes
        back one at a time.
      </p>
      <button
        type="button"
        onClick={() => setAsking(false)}
        className="mt-1.5 text-xs text-shell-text underline-offset-2 hover:underline"
      >
        Not now
      </button>
    </div>
  )
}
